import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  KanbanStageLabel,
  Page,
  PageHeader,
  SearchSelect,
  Switch,
  cardClasses,
  cn,
} from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import type { ClaimAssigneeOption, ClaimRotation } from '@evogard/contract';
import {
  PERIPHERAL_STAGE_COLORS,
  ROTATION_DEPARTMENTS,
  ROTATION_PHASES,
  peripheralPhaseLabel,
  rotationProblems,
  type PeripheralStage,
  type RotationDepartment,
} from '@evogard/domain';
import { useSessionContext } from '../../../lib/session.js';
import { stageColorOf } from '../claim/ClaimsBoard.js';
import { deskOf } from '../claim/desk.js';
import { phaseLabel } from '../claim/labels.js';
import { useRotationPeople, useRotations, useSetRotations } from './useRotations.js';

/** O nome do departamento e da etapa — Periféricos não é mesa do sinistro. */
const departmentName = (d: RotationDepartment) =>
  d === 'perifericos' ? 'Periféricos' : (deskOf(d)?.name ?? d);
const stageLabel = (d: RotationDepartment, phase: string) =>
  d === 'perifericos'
    ? peripheralPhaseLabel(phase as PeripheralStage)
    : phaseLabel(d, phase);
const stageColor = (d: RotationDepartment, phase: string) =>
  d === 'perifericos'
    ? PERIPHERAL_STAGE_COLORS[phase as keyof typeof PERIPHERAL_STAGE_COLORS]
    : stageColorOf(d, phase);

/**
 * Sinistros › Configuração › Rodízio de responsáveis (20/10/2026).
 *
 * Quem recebe o card quando ele entra numa fase. Uma aba por departamento, e
 * nela os rodízios: cada um com as fases dele e as pessoas em ordem. O card
 * que entra numa fase de OUTRO rodízio ganha o próximo da lista; dentro do
 * mesmo rodízio, o responsável não muda (`claims-rotations.ts`).
 *
 * - Análise: um rodízio com todas as fases, e o card fica com a mesma pessoa
 *   do começo ao fim.
 * - Reparo: um rodízio por grupo de fases.
 *
 * Fase fora de rodízio mantém quem está. Vale para quem ENTRAR na fase depois
 * de salvar — ninguém é redistribuído.
 */
export function RotationsScreen() {
  const { data: context } = useSessionContext();
  const rotations = useRotations();
  const people = useRotationPeople();
  const [tab, setTab] = useState<RotationDepartment>('analise');

  return (
    <Page measure="cheia">
      <PageHeader title="Rodízio de responsáveis">
        Quem recebe um sinistro de {context?.currentTenant.nome} quando ele entra numa
        etapa. O responsável aparece no topo do card, e quem trabalha pode trocá-lo à mão.
      </PageHeader>
      {rotations.isError && <Alert tone="erro">{rotations.error.message}</Alert>}
      {people.isError && <Alert tone="erro">{people.error.message}</Alert>}
      {rotations.data && (
        <Tabs value={tab} onValueChange={(v) => setTab(v as RotationDepartment)}>
          <TabsList>
            {ROTATION_DEPARTMENTS.map((d) => (
              <TabsTrigger key={d} value={d}>
                {departmentName(d)}
              </TabsTrigger>
            ))}
          </TabsList>
          {ROTATION_DEPARTMENTS.map((d) => (
            <TabsContent key={d} value={d} className="mt-5">
              <DepartmentRotations
                department={d}
                saved={rotations.data.filter((r) => r.department === d)}
                people={people.data ?? []}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </Page>
  );
}

interface DraftMember {
  userId: string;
  name: string;
  paused: boolean;
  /** Pessoa ativa da base — a inativa fica na lista e não recebe. */
  active: boolean;
}

interface Draft {
  /** A chave da tela; o `id` do banco, ou negativo para o rodízio novo. */
  key: number;
  id: number | null;
  name: string;
  phases: string[];
  members: DraftMember[];
  /** O ponteiro gravado — para dizer quem é o próximo. */
  nextUserId: string | null;
}

const draftOf = (r: ClaimRotation): Draft => ({
  key: r.id,
  id: r.id,
  name: r.name,
  phases: [...r.phases],
  members: r.members.map((m) => ({ ...m })),
  nextUserId: r.nextUserId,
});

/** O que vai ao servidor — e o que se compara para saber se mudou. */
const payloadOf = (drafts: readonly Draft[]) =>
  drafts.map((d) => ({
    id: d.id,
    name: d.name.trim(),
    phases: d.phases,
    members: d.members.map((m) => ({ userId: m.userId, paused: m.paused })),
  }));

/** A lista de pessoas (ordem e pausas) do rascunho difere da gravada? */
const membersChanged = (d: Draft, initial: readonly Draft[]) => {
  const saved = initial.find((i) => i.id !== null && i.id === d.id);
  const shape = (x: Draft) => JSON.stringify(x.members.map((m) => [m.userId, m.paused]));
  return !saved || shape(saved) !== shape(d);
};

function DepartmentRotations({
  department,
  saved,
  people,
}: {
  department: RotationDepartment;
  saved: ClaimRotation[];
  people: readonly ClaimAssigneeOption[];
}) {
  const save = useSetRotations();
  const initial = useMemo(() => saved.map(draftOf), [saved]);
  const [drafts, setDrafts] = useState<Draft[]>(initial);
  const [done, setDone] = useState(false);
  const [nextKey, setNextKey] = useState(-1);
  /* Depois de salvar, a lista relida vira o novo ponto de partida. */
  useEffect(() => setDrafts(initial), [initial]);

  const stages = ROTATION_PHASES[department];
  const dirty = JSON.stringify(payloadOf(drafts)) !== JSON.stringify(payloadOf(initial));
  /* Os problemas com o nome da etapa, e não o código. */
  const problems = rotationProblems(department, payloadOf(drafts)).map((p) =>
    p.replace(/"([a-z_]+)"/g, (whole, code: string) =>
      (stages as readonly string[]).includes(code)
        ? `"${stageLabel(department, code)}"`
        : whole,
    ),
  );
  const ownerOf = (phase: string) => drafts.find((d) => d.phases.includes(phase));
  const loose = stages.filter((p) => !ownerOf(p));

  const update = (key: number, change: (d: Draft) => Draft) => {
    setDone(false);
    save.reset();
    setDrafts(drafts.map((d) => (d.key === key ? change(d) : d)));
  };
  const add = () => {
    setDone(false);
    save.reset();
    /* O primeiro rodízio já nasce com as etapas soltas — o caso da Análise. */
    setDrafts([
      ...drafts,
      {
        key: nextKey,
        id: null,
        name: drafts.length === 0 ? departmentName(department) : '',
        phases: drafts.length === 0 ? [...stages] : [],
        members: [],
        nextUserId: null,
      },
    ]);
    setNextKey(nextKey - 1);
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (problems.length > 0 || !dirty) return;
        save.mutate(
          { department, rotations: payloadOf(drafts) },
          { onSuccess: () => setDone(true) },
        );
      }}
    >
      {drafts.length === 0 ? (
        <Card
          title={`${departmentName(department)} · sem rodízio`}
          descricao="O responsável é escolhido à mão em cada card. Com um rodízio, o card que entra nas etapas dele já chega com responsável."
        >
          <Button type="button" variant="contorno" icon="mais" onClick={add}>
            Criar rodízio
          </Button>
        </Card>
      ) : (
        drafts.map((d, index) => (
          <RotationCard
            key={d.key}
            department={department}
            draft={d}
            edited={membersChanged(d, initial)}
            index={index}
            people={people}
            ownerOf={ownerOf}
            onChange={(change) => update(d.key, change)}
            onRemove={() => {
              setDone(false);
              save.reset();
              setDrafts(drafts.filter((x) => x.key !== d.key));
            }}
          />
        ))
      )}

      {drafts.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-texto-suave">
            {loose.length === 0
              ? 'Todas as etapas estão em algum rodízio.'
              : `${loose.length === 1 ? '1 etapa' : `${loose.length} etapas`} sem rodízio: ${loose
                  .map((p) => stageLabel(department, p))
                  .join(', ')}. Nelas o card mantém o responsável que tem.`}
          </p>
          <Button type="button" variant="contorno" icon="mais" onClick={add}>
            Outro rodízio
          </Button>
        </div>
      )}

      {problems.length > 0 && dirty && (
        <Alert tone="alerta">
          <ul className="flex flex-col gap-1">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* A barra de salvar fica no pé enquanto a tela rola. */}
      <div
        className={cardClasses({
          density: 'densa',
          className:
            'sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3',
        })}
      >
        <p className="text-xs text-texto-suave" role="status">
          {save.isError
            ? save.error.message
            : done
              ? 'Salvo. Vale para o card que entrar numa etapa daqui em diante.'
              : dirty
                ? 'Alterações não salvas. Quem já está numa etapa continua com o responsável que tem.'
                : 'Ao entrar numa etapa de outro rodízio, o card vai para o próximo da lista. Dentro do mesmo rodízio, o responsável não muda; voltando a um rodízio, ele volta para quem o tinha.'}
        </p>
        <div className="flex gap-2">
          {dirty && (
            <Button
              type="button"
              variant="secundario"
              disabled={save.isPending}
              onClick={() => {
                setDrafts(initial);
                setDone(false);
              }}
            >
              Desfazer
            </Button>
          )}
          <Button
            type="submit"
            loading={save.isPending}
            disabled={problems.length > 0 || !dirty}
          >
            Salvar
          </Button>
        </div>
      </div>
    </form>
  );
}

function RotationCard({
  department,
  draft,
  edited,
  index,
  people,
  ownerOf,
  onChange,
  onRemove,
}: {
  department: RotationDepartment;
  draft: Draft;
  /** A lista ou as pausas mudaram desde o que está gravado. */
  edited: boolean;
  index: number;
  people: readonly ClaimAssigneeOption[];
  ownerOf: (phase: string) => Draft | undefined;
  onChange: (change: (d: Draft) => Draft) => void;
  onRemove: () => void;
}) {
  const stages = ROTATION_PHASES[department];
  const listed = new Set(draft.members.map((m) => m.userId));
  const candidates = people.filter((p) => !listed.has(p.id));
  /* Quem recebe o próximo é conta do servidor, com o ponteiro gravado: com a
     lista mexida e não salva, a tela não adivinha. */
  const next = edited ? null : draft.nextUserId;
  const nextName = draft.members.find((m) => m.userId === next)?.name;
  const move = (from: number, to: number) =>
    onChange((d) => {
      const members = [...d.members];
      const [m] = members.splice(from, 1);
      members.splice(to, 0, m!);
      return { ...d, members };
    });
  const title = draft.name.trim() || `Rodízio ${index + 1}`;

  return (
    <Card
      title={title}
      descricao={
        draft.phases.length === 0
          ? 'Nenhuma etapa ainda — marque abaixo onde este rodízio distribui.'
          : draft.members.length === 0
            ? 'Ninguém na lista: o card entra nas etapas e fica sem responsável novo.'
            : edited
              ? 'Quem recebe o próximo card aparece depois de salvar.'
              : nextName
                ? `O próximo card vai para ${nextName}.`
                : 'Todos da lista estão pausados ou inativos: o card fica com quem estava.'
      }
      actions={
        <Button
          type="button"
          variant="discreto"
          size="sm"
          icon="lixeira"
          onClick={onRemove}
        >
          Tirar rodízio
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <Field
          label="Nome"
          value={draft.name}
          maxLength={60}
          placeholder="Entrada e orçamentação"
          onChange={(e) => {
            const name = e.target.value;
            onChange((d) => ({ ...d, name }));
          }}
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-semibold text-texto">Etapas</legend>
          <div className="flex flex-wrap gap-2">
            {stages.map((phase) => {
              const mine = draft.phases.includes(phase);
              const other = mine ? undefined : ownerOf(phase);
              const otherName = other ? other.name.trim() || 'outro rodízio' : null;
              return (
                <button
                  key={phase}
                  type="button"
                  aria-pressed={mine}
                  title={
                    otherName
                      ? `Está em ${otherName}. Marcar aqui tira de lá.`
                      : undefined
                  }
                  onClick={() =>
                    onChange((d) => ({
                      ...d,
                      phases: mine
                        ? d.phases.filter((p) => p !== phase)
                        : stages.filter((p) => p === phase || d.phases.includes(p)),
                    }))
                  }
                  className={cn(
                    'flex items-center gap-2 rounded-pilula border px-3 py-1.5 text-xs font-medium transition-colors',
                    mine
                      ? 'border-transparent bg-chrome-alto text-chrome-texto'
                      : otherName
                        ? 'border-dashed border-borda text-texto-tenue hover:text-texto'
                        : 'border-borda text-texto-suave hover:border-texto-suave hover:text-texto',
                  )}
                >
                  <KanbanStageLabel
                    stage={{
                      name: stageLabel(department, phase),
                      color: stageColor(department, phase),
                    }}
                  />
                  {otherName && <span className="text-texto-tenue">· {otherName}</span>}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="flex flex-col gap-2">
          <h4 className="text-sm font-semibold text-texto">Pessoas, na ordem da vez</h4>
          {draft.members.length > 0 && (
            <ol className="flex flex-col divide-y divide-borda rounded-borda border border-borda">
              {draft.members.map((m, i) => (
                <li
                  key={m.userId}
                  className="flex flex-wrap items-center gap-3 px-3 py-2"
                >
                  <span className="w-5 text-xs font-semibold tabular-nums text-texto-tenue">
                    {i + 1}
                  </span>
                  <span
                    className={cn(
                      'min-w-0 flex-1 font-medium',
                      (m.paused || !m.active) && 'text-texto-tenue',
                    )}
                  >
                    {m.name}
                  </span>
                  {m.userId === next && <Badge tone="informacao">próximo</Badge>}
                  {!m.active && <Badge tone="alerta">inativo</Badge>}
                  <Switch
                    label={`Recebe card — ${m.name}`}
                    onLabel="Na vez"
                    offLabel="Pausado"
                    checked={!m.paused}
                    onCheckedChange={(on) =>
                      onChange((d) => ({
                        ...d,
                        members: d.members.map((x) =>
                          x.userId === m.userId ? { ...x, paused: !on } : x,
                        ),
                      }))
                    }
                  />
                  <span className="flex gap-1">
                    <Button
                      type="button"
                      variant="discreto"
                      size="sm"
                      icon="seta-cima"
                      aria-label={`Subir ${m.name}`}
                      disabled={i === 0}
                      onClick={() => move(i, i - 1)}
                    />
                    <Button
                      type="button"
                      variant="discreto"
                      size="sm"
                      icon="seta-baixo"
                      aria-label={`Descer ${m.name}`}
                      disabled={i === draft.members.length - 1}
                      onClick={() => move(i, i + 1)}
                    />
                    <Button
                      type="button"
                      variant="discreto"
                      size="sm"
                      icon="fechar"
                      aria-label={`Tirar ${m.name} do rodízio`}
                      onClick={() =>
                        onChange((d) => ({
                          ...d,
                          members: d.members.filter((x) => x.userId !== m.userId),
                        }))
                      }
                    />
                  </span>
                </li>
              ))}
            </ol>
          )}
          <div className="max-w-sm">
            <SearchSelect
              label="Pôr pessoa no rodízio"
              hiddenLabel
              placeholder="Pôr pessoa no rodízio"
              searchPlaceholder="Buscar pelo nome"
              emptyMessage="Ninguém mais nesta base."
              options={candidates.map((p) => ({ value: p.id, label: p.name }))}
              value=""
              onChange={(userId) => {
                const person = people.find((p) => p.id === userId);
                if (!person) return;
                onChange((d) => ({
                  ...d,
                  members: [
                    ...d.members,
                    { userId: person.id, name: person.name, paused: false, active: true },
                  ],
                }));
              }}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
